import { PayloadAction } from '@reduxjs/toolkit'
import { takeEvery, all, put, call, select, takeLatest, debounce, spawn } from 'redux-saga/effects'
import { renameDataset as renameKeplerDataset } from '@carto/kepler.gl/actions'
import { watchDuplicateMap } from 'features/maps/state/duplicateMapSagas'
import {
  setMyMapsStatus,
  setCurrentMap,
  setCurrentMapDatasets,
  updateCurrentMapRequest,
  mapDatasetsRequest,
  editMapTitleDescription,
  setEditMapError,
  setEditMapStatus,
  updateCurrentMap,
  changePrivacyMapRequest,
  setChangeMapPrivacyStatus,
  setCurrentMapStatus,
  fetchLatestMapsRequest,
  setLatestMapsStatus,
  setLatestMaps,
  setLatestMapsError,
  fetchDemoMapsRequest,
  setDemoMaps,
  setDemoMapsStatus,
  setDemoMapsError,
  createDemoMapRequest,
  setCreateDemoMapError,
  addCreatingDemoMapsId,
  removeCreatingDemoMapsId,
  setChangeMapPrivacyError,
  setMapSettings,
  renameDatasetRequest,
  updateCurrentMapDataset,
  patchMapRequest,
  defaultMapSettings,
  setLastSavedMapConfig,
  editMapWorkspace,
  setAgentEnabledForViewer,
  setAgentConfig,
  deleteAgent
} from './mapsSlice'
import { setStatsStatus } from 'features/account/state/accountSlice'
import {
  getMaps,
  updateMap,
  getMap,
  getMapDatasets,
  patchDataset,
  setMapPrivacy,
  getPublicMap,
  getDemoMaps,
  createDemoMap,
  lockMap
} from '../api/maps'
import {
  CartoKeplerMapConfigEnvelope,
  DemoMap,
  Map,
  MapDataset,
  MapDetailsRequest,
  MapFieldToOrderBy,
  MapLockStatus,
  MapPrivacy,
  SetMapPrivacyRequest
} from '../types'
import { ApiError, PaginatedResponse, RequestStatus, SortDirection } from 'features/common'
import { setCurrentMapLockStatus, showGlobalBuilderError } from 'features/builder/state/builderSlice'
import { createApiError } from 'features/common/api/apiError'
import { isHardQuotaError } from 'features/quotas/utils'
import { getMapConfig } from 'features/builder/state/builderSagas'
import { BuilderGenAIEnvironment, Widget, WidgetType } from 'features/builder'
import { sendMetricEvent } from 'features/metrics/model/metricsMutations'
import { MetricEventType } from 'features/metrics/api/metrics'
import { ObjectMetadataContext } from 'features/metrics/types'
import { openNewWindow } from 'features/common/utils/navigationUtils'
import { BuilderPaths } from 'features/builder/paths'
import { runTransactionSaga } from 'features/common/utils/transactions'
import { fetchQueryEffect, queryClient } from 'queryClient'
import { tagsQueryKey } from 'features/tags/api/tagsQueries'
import {
  accountBuilderGenAISettingsQueryV0,
  accountBuilderGenAISettingsQueryV1
} from 'features/account/api/accountQueries'
import { BuilderGenAISettings } from 'features/account/types'
import { updateAIMapAgent } from 'features/builder/model/AIMapAgent'
import { AlertSeverity, showNotification } from 'features/notifications/state/notificationSlice'
import { updateLayerCustomMarkers } from 'features/builder/state/updateLayerCustomMarkers'
import { FeatureFlag } from 'app/types'
import { getFeatureFlag } from 'features/common/state/commonSlice'

const LATEST_MAPS_FILTER = {
  search: undefined,
  order: {
    by: MapFieldToOrderBy.UpdatedAt,
    direction: SortDirection.Desc
  },
  filter: {
    privacy: undefined
  },
  pagination: {
    size: 4,
    page: 1
  }
}

function* fetchDemoMaps() {
  try {
    yield put(setDemoMapsStatus(RequestStatus.Pending))
    const maps: Array<DemoMap> = yield call(getDemoMaps)
    yield put(setDemoMaps(maps))
    yield put(setDemoMapsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setDemoMapsStatus(RequestStatus.Rejected))
    yield put(setDemoMapsError(e as ApiError))
  }
}

function* postDemoMap(action: PayloadAction<{ id: string; url: string }>) {
  const { id, url } = action.payload
  try {
    yield put(addCreatingDemoMapsId(id))
    const createdMap: Map = yield call(createDemoMap, url)
    yield put(setCurrentMap(createdMap))
    openNewWindow(BuilderPaths.EditMap(createdMap.id))
    yield put(removeCreatingDemoMapsId(id))
  } catch (e) {
    yield put(removeCreatingDemoMapsId(id))
    const apiError = createApiError(e)
    yield put(setCreateDemoMapError(apiError))

    if (isHardQuotaError(apiError)) {
      yield put(setStatsStatus(RequestStatus.Idle))
    }
  }
}

export function* saveCurrentMap(silently = false) {
  yield runTransactionSaga('saveCurrentMap', function* saveCurrentMapInt() {
    try {
      yield put(setEditMapStatus(RequestStatus.Pending))
      const map: Map | undefined = yield select((state) => state.maps.currentMap)
      if (!map?.id) return

      const keplerMapConfig: CartoKeplerMapConfigEnvelope = yield getMapConfig()
      const keplerMapConfigPatched = updateLayerCustomMarkers(keplerMapConfig)
      const updatedMap = { ...map, keplerMapConfig: keplerMapConfigPatched }

      const responseMap: Map = yield call(updateMap, updatedMap)
      yield put(setLastSavedMapConfig(responseMap.keplerMapConfig?.config))
      if (!silently) {
        yield put(updateCurrentMap(responseMap))
      }

      yield put(setEditMapStatus(RequestStatus.Fulfilled))
      yield put(setMyMapsStatus(RequestStatus.Idle))
      yield put(setLatestMapsStatus(RequestStatus.Idle))
    } catch (e) {
      yield put(setEditMapStatus(RequestStatus.Idle))
      yield put(showGlobalBuilderError((e as Error).message))
    }
  })
}

/* eslint-disable redux-saga/no-unhandled-errors */
function* handleUpdateMap(action: PayloadAction<Map>) {
  const { id } = action.payload
  const currentMap: Map = yield select((state) => state.maps.currentMap)
  if (id === currentMap?.id) {
    yield put(setEditMapStatus(RequestStatus.Pending))
  }

  const updatedMap: Map = yield call(updateMap, action.payload)
  if (currentMap?.id === updatedMap.id) {
    const currentMapLockStatus: MapLockStatus = yield select((state) => state.builder.currentMapLockStatus)
    if (currentMapLockStatus?.locked && !updatedMap.collaborative) {
      yield put(setCurrentMapLockStatus({ locked: false }))
    } else if (!currentMapLockStatus?.locked && updatedMap.collaborative) {
      yield call(lockMap, { mapId: id as string })
    }
    yield put(updateCurrentMap(updatedMap))
    yield put(setMapSettings(updatedMap.keplerMapConfig?.config.mapSettings || defaultMapSettings))
  }
  queryClient.invalidateQueries(tagsQueryKey.get, { exact: false })
  yield put(setEditMapStatus(RequestStatus.Fulfilled))
  yield put(setMyMapsStatus(RequestStatus.Idle))
  yield put(setLatestMapsStatus(RequestStatus.Idle))
  yield put(setEditMapStatus(RequestStatus.Idle))
}

function* handlePatchMap(action: PayloadAction<Map>) {
  try {
    yield call(handleUpdateMap, action)
  } catch (e) {
    yield put(showGlobalBuilderError((e as Error).message))
  }
}

function* handlePatchMapWorkspace(action: PayloadAction<Map>) {
  try {
    yield call(handleUpdateMap, action)
  } catch (e) {
    yield put(setEditMapError(e as ApiError))
  }
}

export function* fetchMapDetails(request: MapDetailsRequest) {
  const { id, privacy } = request
  try {
    yield put(setCurrentMapStatus(RequestStatus.Pending))

    const [mapDatasets, map]: [mapDatasets: Array<MapDataset>, map: Map] =
      privacy === MapPrivacy.Private
        ? yield all([(yield call(getMapDatasets, id)) as Array<MapDataset>, (yield call(getMap, id)) as Map])
        : yield call(getPublicMap, id)

    yield put(setCurrentMapDatasets(mapDatasets))
    yield put(setCurrentMap(map))

    if (map.lockStatus) {
      yield put(setCurrentMapLockStatus(map.lockStatus))
    }

    yield put(setCurrentMapStatus(RequestStatus.Fulfilled))
    return
  } catch (e) {
    const apiError = createApiError(e)
    yield put(setCurrentMapStatus(RequestStatus.Rejected))
    return apiError
  }
}

function* fetchMapDatasets(action: PayloadAction<string>) {
  try {
    yield put(setCurrentMapStatus(RequestStatus.Pending))
    const mapDatasets: Array<MapDataset> = yield call(getMapDatasets, action.payload)
    yield put(setCurrentMapDatasets(mapDatasets))
    yield put(setCurrentMapStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setCurrentMapStatus(RequestStatus.Rejected))
    yield put(showGlobalBuilderError((e as Error).message))
  }
}

function getTotalWidgetsByType(widgets?: Widget[]) {
  const totalWidgetsByType = {
    [WidgetType.FORMULA]: 0,
    [WidgetType.CATEGORY]: 0,
    [WidgetType.HISTOGRAM]: 0,
    [WidgetType.TABLE]: 0,
    [WidgetType.TIMESERIES]: 0,
    [WidgetType.RANGE]: 0
  }
  if (!widgets) return totalWidgetsByType
  for (const widget of widgets) {
    const widgetType = widget.type
    if (totalWidgetsByType[widgetType]) {
      totalWidgetsByType[widgetType] += 1
    } else {
      totalWidgetsByType[widgetType] = 1
    }
  }
  return totalWidgetsByType
}

export function* changeMapPrivacy({
  mapId,
  privacy,
  groupIds,
  userIds,
  sharingScope,
  publicPassword
}: SetMapPrivacyRequest) {
  try {
    const map: Map = yield select((state) => state.maps.currentMap)
    const response: Record<string, string> = yield call(setMapPrivacy, {
      mapId,
      privacy,
      publicPassword,
      groupIds,
      userIds,
      sharingScope
    })

    if (map && map.id === mapId) {
      const currentMapUpdate: Partial<Map> = {
        privacy,
        sharingScope,
        token: response.token,
        lastPublishedAt: response.lastPublishedAt
      }

      if (privacy === MapPrivacy.Public && publicPassword !== undefined) {
        currentMapUpdate.publishedWithPassword = !!publicPassword
        currentMapUpdate.publicMapPassword = publicPassword ?? undefined
      }
      if (map.sharingScope !== sharingScope) {
        currentMapUpdate.sharingScope = sharingScope
      }

      yield put(updateCurrentMap(currentMapUpdate))
      sendMetricEvent(MetricEventType.MapUpdatesPublished, {
        context: ObjectMetadataContext.Builder,
        widgets: getTotalWidgetsByType(map.keplerMapConfig?.config.widgets),
        mapId
      })

      // The AI Agent creation is spawned as a separate process to avoid blocking map publication because it sometimes takes too long
      // It has been a product decision the user doesn't receive feedback during the Agent creation and only will receive if it fails
      yield spawn(updateAgent, { map, environment: BuilderGenAIEnvironment.Live })
    }

    yield put(setMyMapsStatus(RequestStatus.Idle))
    yield put(setLatestMapsStatus(RequestStatus.Idle))
    yield put(setStatsStatus(RequestStatus.Idle))
  } catch (e) {
    const apiError = createApiError(e)
    if (isHardQuotaError(apiError)) {
      yield put(setStatsStatus(RequestStatus.Idle))
    }
    throw e
  }
}

function* updateAgent({ map, environment }: { map: Map; environment: BuilderGenAIEnvironment }) {
  try {
    if (!map.keplerMapConfig?.config.aiSettings?.enabled) return

    const isV1 = yield select((state) => getFeatureFlag(state, FeatureFlag.GenAIV1))
    const accountBuilderGenAISettings: BuilderGenAISettings = yield fetchQueryEffect(
      isV1 ? accountBuilderGenAISettingsQueryV1 : accountBuilderGenAISettingsQueryV0
    )

    if (!accountBuilderGenAISettings.enabled) return

    yield call(updateAIMapAgent, environment)
  } catch (e) {
    console.error(e)
    yield put(showNotification({ msg: 'builder.aiManager.agent.error', formatted: true, type: AlertSeverity.Error }))
    throw e
  }
}

function* handleAgentUpdate() {
  try {
    yield call(saveCurrentMap)
  } catch (e) {
    console.error(e)
  }
}

function* handleChangeMapPrivacy(action: PayloadAction<SetMapPrivacyRequest>) {
  try {
    yield put(setEditMapStatus(RequestStatus.Pending))
    yield put(setChangeMapPrivacyStatus(RequestStatus.Pending))

    yield call(changeMapPrivacy, action.payload)

    yield put(setEditMapStatus(RequestStatus.Fulfilled))
    yield put(setChangeMapPrivacyStatus(RequestStatus.Fulfilled))
    yield put(setEditMapStatus(RequestStatus.Idle))
    yield put(setChangeMapPrivacyStatus(RequestStatus.Idle))
    yield put(setMyMapsStatus(RequestStatus.Idle))
    yield put(setLatestMapsStatus(RequestStatus.Idle))
    yield put(setStatsStatus(RequestStatus.Idle))
  } catch (e) {
    yield put(setChangeMapPrivacyStatus(RequestStatus.Rejected))
    const apiError = createApiError(e)
    yield put(setChangeMapPrivacyError(apiError))
    if (isHardQuotaError(apiError)) {
      yield put(setStatsStatus(RequestStatus.Idle))
    }
  }
}

function* fetchLatestMaps() {
  try {
    yield put(setLatestMapsStatus(RequestStatus.Pending))
    const mapsResponse: PaginatedResponse<Map> = yield call(getMaps, LATEST_MAPS_FILTER)
    yield put(setLatestMaps(mapsResponse.data))
    yield put(setLatestMapsStatus(RequestStatus.Fulfilled))
  } catch (e) {
    yield put(setLatestMapsStatus(RequestStatus.Rejected))
    yield put(setLatestMapsError(e as ApiError))
  }
}

function* renameDataset(action: PayloadAction<{ mapId: string; datasetId: string; label: string }>) {
  try {
    yield call(patchDataset, {
      mapId: action.payload.mapId,
      datasetId: action.payload.datasetId,
      body: { label: action.payload.label }
    })
    yield put(
      updateCurrentMapDataset({
        id: action.payload.datasetId,
        dataset: { label: action.payload.label }
      })
    )
    yield put(renameKeplerDataset(action.payload.datasetId, action.payload.label))
  } catch (e) {
    console.error(e)
  }
}

function* watchFetchmaps() {
  yield takeEvery(updateCurrentMapRequest.type, saveCurrentMap)
  yield takeEvery(patchMapRequest.type, handlePatchMap)
  yield takeEvery(mapDatasetsRequest.type, fetchMapDatasets)
  yield takeEvery(changePrivacyMapRequest.type, handleChangeMapPrivacy)
  yield takeLatest(fetchLatestMapsRequest.type, fetchLatestMaps)
  yield takeEvery(fetchDemoMapsRequest.type, fetchDemoMaps)
  yield takeEvery(createDemoMapRequest.type, postDemoMap)
  yield takeEvery(renameDatasetRequest.type, renameDataset)
  yield takeEvery(setAgentEnabledForViewer.type, handleAgentUpdate)
  yield takeEvery(setAgentConfig.type, handleAgentUpdate)
  yield takeEvery(deleteAgent.type, handleAgentUpdate)

  try {
    yield debounce(1000, editMapTitleDescription, handlePatchMap)
    yield debounce(1000, editMapWorkspace, handlePatchMapWorkspace)
  } catch (e) {
    yield put(showGlobalBuilderError((e as Error).message))
  }
}

export default function* mapsSaga() {
  try {
    yield all([watchFetchmaps(), watchDuplicateMap()])
  } catch (e) {
    const msg = `Error setting up mapsSaga ${(e as Error).message}`
    yield put(showGlobalBuilderError(msg))
  }
}
